import { supabase } from '../lib/supabase';
import { PAYPAL_PACKAGES } from '../config/paypal';

export interface PayPalTransaction {
  orderId: string;
  payerId: string;
  payerEmail: string;
  amount: number;
  currency: string;
  status: string;
}

/**
 * Guarda una transacción de PayPal en la base de datos
 */
async function savePayPalTransaction(
  userId: string,
  packageId: string,
  amount: number,
  currency: string,
  orderId: string,
  payerId: string,
  payerEmail: string
) {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .insert({
        user_id: userId,
        amount: amount,
        currency: currency,
        status: 'completed',
        payment_method: 'paypal',
        transaction_id: orderId,
        metadata: {
          package_id: packageId,
          payer_id: payerId,
          payer_email: payerEmail,
          payment_gateway: 'paypal'
        }
      })
      .select()
      .single();

    if (error) {
      console.error('[PayPal] Error al guardar transacción:', error);
      throw error;
    }

    console.log('[PayPal] Transacción guardada:', data);
    return data;
  } catch (error) {
    console.error('[PayPal] Error en savePayPalTransaction:', error);
    throw error;
  }
}

/**
 * Acredita VCoins al usuario después de un pago exitoso
 */
async function creditVCoins(userId: string, vcoins: number) {
  try {
    // 1. Obtener balance actual
    const { data: userData, error: fetchError } = await supabase
      .from('users')
      .select('vcoins')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('[PayPal] Error al obtener balance actual:', fetchError);
      throw fetchError;
    }

    const currentBalance = userData?.vcoins || 0;
    const newBalance = currentBalance + vcoins;

    // 2. Actualizar balance
    const { data, error: updateError } = await supabase
      .from('users')
      .update({ vcoins: newBalance })
      .eq('id', userId)
      .select()
      .single();

    if (updateError) {
      console.error('[PayPal] Error al actualizar balance:', updateError);
      throw updateError;
    }

    console.log(`[PayPal] VCoins acreditadas: ${vcoins} (Balance: ${currentBalance} → ${newBalance})`);
    return data;
  } catch (error) {
    console.error('[PayPal] Error en creditVCoins:', error);
    throw error;
  }
}

/**
 * Procesa un pago exitoso de PayPal
 *
 * Esta función se llama desde el componente PayPalButtons
 * cuando el usuario completa el pago.
 */
export async function processPayPalPayment(
  userId: string,
  packageId: string,
  transaction: PayPalTransaction
): Promise<{ success: boolean; message: string; vcoins?: number }> {
  try {
    console.log('[PayPal] Procesando pago:', { userId, packageId, transaction });

    // 1. Buscar el paquete
    const selectedPackage = PAYPAL_PACKAGES.find(pkg => pkg.id === packageId);
    if (!selectedPackage) {
      throw new Error(`Paquete no encontrado: ${packageId}`);
    }

    // 2. Verificar que el monto sea correcto
    if (Math.abs(transaction.amount - selectedPackage.amount_usd) > 0.01) {
      throw new Error(
        `Monto incorrecto. Esperado: $${selectedPackage.amount_usd}, Recibido: $${transaction.amount}`
      );
    }

    // 3. Guardar transacción en BD
    await savePayPalTransaction(
      userId,
      packageId,
      transaction.amount,
      transaction.currency,
      transaction.orderId,
      transaction.payerId,
      transaction.payerEmail
    );

    // 4. Acreditar VCoins al usuario
    await creditVCoins(userId, selectedPackage.vcoins);

    console.log(`[PayPal] ✅ Pago procesado exitosamente: ${selectedPackage.vcoins} VCoins acreditadas`);

    return {
      success: true,
      message: `¡Pago exitoso! ${selectedPackage.vcoins} VCoins acreditadas a tu cuenta.`,
      vcoins: selectedPackage.vcoins
    };
  } catch (error: any) {
    console.error('[PayPal] ❌ Error al procesar pago:', error);
    return {
      success: false,
      message: error.message || 'Error al procesar el pago. Por favor contacta a soporte.'
    };
  }
}

/**
 * Obtiene un paquete por su ID
 */
export function getPayPalPackage(packageId: string) {
  return PAYPAL_PACKAGES.find(pkg => pkg.id === packageId);
}

/**
 * Calcula la comisión de PayPal para un monto dado
 */
export function calculatePayPalFee(amount: number): number {
  const COMMISSION_PERCENTAGE = 0.054; // 5.4%
  const COMMISSION_FIXED = 0.30; // $0.30 USD
  return (amount * COMMISSION_PERCENTAGE) + COMMISSION_FIXED;
}

/**
 * Calcula el monto neto que recibes después de comisiones de PayPal
 */
export function calculateNetAmount(amount: number): number {
  return amount - calculatePayPalFee(amount);
}
